import { QuestionAnswer } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Widget } from '~/components/MyWorkspace/Widget';
import {
  SendMessageToHRDialog,
  useSendMessageToHRDialog,
} from '~/features/SendMessageToHRDialog';

import { WidgetProps } from '../../data/widgets';

import { AllAnnouncementsDialog } from './components/AllAnnouncementsDialog';
import { Content } from './components/Content';
import { useAnnouncementsWidget } from './useAnnouncementsWidget';
import { useSxStyles } from './useStyles';

export interface Props extends WidgetProps {}

export const AnnouncementsWidget: React.FC<Props> = ({ title }) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    announcements,
    visibleAnnouncement,
    loading,
    failedToLoad,
    showAllAnnouncementsDialog,
    handleReadAllButtonClick,
    handleShowAllAnnouncementsDialogClose,
    handleContentOnMouseEnter,
    handleContentOnMouseLeave,
    handleContentOnReadMoreDialogOpen,
    handleContentOnReadMoreDialogClose,
  } = useAnnouncementsWidget();

  const messageToHRDialog = useSendMessageToHRDialog();

  return (
    <>
      <Widget
        title={title || t('announcements')}
        content={
          <Box
            onMouseEnter={handleContentOnMouseEnter}
            onMouseLeave={handleContentOnMouseLeave}
            sx={sxStyles.content}
          >
            <Content
              loading={loading}
              failedToLoad={failedToLoad}
              announcement={visibleAnnouncement}
              announcements={announcements}
              onReadMoreDialogOpen={handleContentOnReadMoreDialogOpen}
              onReadMoreDialogClose={handleContentOnReadMoreDialogClose}
            />
          </Box>
        }
        actions={
          <>
            <Button
              color="primary"
              size="small"
              disabled={announcements.length <= 1}
              onClick={handleReadAllButtonClick}
              data-cy="read-all"
            >
              {t('readAll')}
            </Button>
            {messageToHRDialog.isAvailable && (
              <Button
                color="primary"
                size="small"
                startIcon={<QuestionAnswer />}
                onClick={messageToHRDialog.onOpen}
                disabled={messageToHRDialog.isDisabled}
                data-cy={'message-to-hr'}
              >
                {t('messageToHR')}
              </Button>
            )}
          </>
        }
        dataCy="announcements-widget"
      />

      <AllAnnouncementsDialog
        announcements={announcements}
        open={showAllAnnouncementsDialog}
        onClose={handleShowAllAnnouncementsDialogClose}
      />

      <SendMessageToHRDialog {...messageToHRDialog.dialogProps} />
    </>
  );
};
